"use client";

import { Fragment, useRef, useState } from "react";
import { ArrowRight, Check, Minus, Table2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { MATRIX_GROUPS, MATRIX_LIMITS, MATRIX_TIERS } from "./pricingMatrix";

// Total tool rows surfaced in the trigger microcopy ("All N tools ...").
const TOOL_COUNT = MATRIX_GROUPS.reduce((sum, group) => sum + group.tools.length, 0);

// The `--lb-*` design tokens are scoped to `.lb-page` in globals.css, but the
// dialog & sheet render in a portal on <body> (OUTSIDE `.lb-page`) where those
// vars are undefined — so hsl(var(--lb-panel)) is invalid and the surface paints
// see-through. The `.lb-tokens` class re-declares the tokens (it's the token-only
// scope `.lb-page` extends), and — unlike hardcoded inline values — it's picked
// up by the `.dark .lb-tokens` and `html[data-palette="cobalt"] .lb-tokens`
// overrides, so the overlay tracks light/dark AND the active palette.
const LB_TOKENS = "lb-tokens";

// Sticky first column. Opaque lb-panel (matching the table surface below) so it
// cleanly covers horizontally-scrolled cells on narrow screens.
const STICKY_COL =
  "sticky left-0 z-10 whitespace-nowrap bg-[hsl(var(--lb-panel))] px-5 text-left text-sm text-[hsl(var(--lb-ink))]";

function GroupHeading({ label }: { label: string }) {
  return (
    <tr>
      <td
        colSpan={MATRIX_TIERS.length + 1}
        className="border-t border-[hsl(var(--lb-line))] bg-[hsl(var(--lb-bg-soft)/0.35)] px-5 pb-2 pt-4"
      >
        {/* sticky so the category label stays visible while scrolling on mobile */}
        <span className="sticky left-0 inline-block font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--lb-subtle))]">
          {label}
        </span>
      </td>
    </tr>
  );
}

// The table is identical across the desktop modal and the mobile sheet — define
// it once so both surfaces stay in sync and there's no duplicated markup.
function ComparisonTable() {
  return (
    <table aria-label="Plan feature comparison" className="w-full min-w-[820px] border-collapse">
      <thead>
        <tr>
          <th scope="col" className={cn(STICKY_COL, "py-5 font-medium text-[hsl(var(--lb-muted))]")}>
            <span className="sr-only">Feature</span>
          </th>
          {MATRIX_TIERS.map((tier) => (
            <th
              key={tier.key}
              scope="col"
              className={cn(
                "px-4 py-5 text-center align-bottom",
                tier.featured && "bg-[hsl(var(--lb-clay)/0.1)]",
              )}
            >
              <span className="block text-[15px] font-semibold tracking-[-0.02em] text-[hsl(var(--lb-clay))]">
                {tier.name}
              </span>
              {tier.featured ? (
                <span className="mt-2 inline-block rounded-full border border-[hsl(var(--lb-clay)/0.45)] bg-[hsl(var(--lb-clay)/0.16)] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--lb-ink))]">
                  Popular
                </span>
              ) : null}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {MATRIX_GROUPS.map((group) => (
          <Fragment key={group.category}>
            <GroupHeading label={group.category} />
            {group.tools.map((tool) => (
              <tr key={tool.name} className="border-t border-[hsl(var(--lb-line)/0.5)]">
                <th scope="row" className={cn(STICKY_COL, "py-3 font-normal")}>
                  {tool.name}
                </th>
                {MATRIX_TIERS.map((tier) => {
                  const label = tool.cellLabel?.[tier.key];
                  const included = tool.tiers.includes(tier.key);
                  return (
                    <td
                      key={tier.key}
                      className={cn(
                        "px-4 py-3 text-center",
                        tier.featured && "bg-[hsl(var(--lb-clay)/0.06)]",
                      )}
                    >
                      {label ? (
                        <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-[hsl(var(--lb-clay))]">
                          {label}
                        </span>
                      ) : included ? (
                        <Check className="mx-auto h-4 w-4 text-[hsl(var(--lb-clay))]" />
                      ) : (
                        <Minus className="mx-auto h-3.5 w-3.5 text-[hsl(var(--lb-subtle)/0.45)]" />
                      )}
                      <span className="sr-only">{label ?? (included ? "Included" : "Not included")}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </Fragment>
        ))}

        <GroupHeading label="Plans & limits" />
        {MATRIX_LIMITS.map((row) => (
          <tr key={row.label} className="border-t border-[hsl(var(--lb-line)/0.5)]">
            <th scope="row" className={cn(STICKY_COL, "py-3 font-normal")}>
              {row.label}
            </th>
            {MATRIX_TIERS.map((tier) => (
              <td
                key={tier.key}
                className={cn(
                  "px-4 py-3 text-center text-sm font-medium text-[hsl(var(--lb-muted))]",
                  tier.featured && "bg-[hsl(var(--lb-clay)/0.06)]",
                )}
              >
                {row.values[tier.key] ?? "—"}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Tailwind `sm` breakpoint. Below this we open the bottom Sheet; at/above it the
// centered Dialog. Decided at open-time so only ONE overlay (one focus trap, one
// aria-modal) is ever mounted — CSS-hiding the other surface would leave a second
// live focus trap in the DOM.
const SM_BREAKPOINT = 640;

export function PricingComparisonMatrix({
  className,
}: {
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  // null = closed. "dialog" | "sheet" = which surface to mount while open.
  const [surface, setSurface] = useState<"dialog" | "sheet" | null>(null);
  // The trigger is a plain <button>, not a Radix Trigger, so Radix won't know
  // where to send focus on close — restore it ourselves via onCloseAutoFocus.
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleOpen = () => {
    const isMobile =
      typeof window !== "undefined" &&
      window.matchMedia(`(max-width: ${SM_BREAKPOINT - 1}px)`).matches;
    setSurface(isMobile ? "sheet" : "dialog");
    setOpen(true);
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setSurface(null);
  };

  // Send focus back to the trigger band when either overlay closes (Esc, X, or
  // overlay click), instead of letting it fall back to <body>.
  const restoreFocus = (event: Event) => {
    event.preventDefault();
    triggerRef.current?.focus();
  };

  return (
    <>
      {/* Compact pill trigger — styled like the old "See the full breakdown"
          chip rather than a full-width band. The full count lives in the modal. */}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleOpen}
        aria-haspopup="dialog"
        aria-label="Compare plans"
        className={cn(
          "group inline-flex w-fit items-center gap-2 rounded-full border border-[hsl(var(--lb-clay)/0.5)] bg-[hsl(var(--lb-clay)/0.16)] px-4 py-2 text-sm font-semibold text-[hsl(var(--lb-clay))] transition hover:bg-[hsl(var(--lb-clay)/0.24)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--lb-clay)/0.7)] focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(var(--lb-bg))]",
          className ?? "mt-8",
        )}
      >
        <Table2 className="h-4 w-4" />
        Compare plans
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </button>

      {/* DESKTOP REVEAL (sm+) — large centered modal. Header is fixed; the table
          area is the only thing that scrolls (vertical + horizontal), so the
          modal stays put and the page underneath never grows. */}
      <DialogPrimitive.Root open={open && surface === "dialog"} onOpenChange={handleOpenChange}>
        <DialogContent
          onCloseAutoFocus={restoreFocus}
          className={cn(
            LB_TOKENS,
            "flex max-h-[85vh] w-[min(64rem,92vw)] max-w-none flex-col gap-0 overflow-hidden rounded-[1.5rem] border-[hsl(var(--lb-line))] bg-[hsl(var(--lb-panel))] p-0 text-[hsl(var(--lb-ink))] sm:rounded-[1.5rem]",
          )}
        >
          <DialogHeader className="space-y-1 border-b border-[hsl(var(--lb-line))] px-5 py-5 pr-14 text-left sm:px-6 sm:pr-14">
            <DialogTitle className="text-lg font-semibold tracking-[-0.01em] text-[hsl(var(--lb-ink))]">
              Plan comparison
            </DialogTitle>
            <DialogDescription className="text-sm text-[hsl(var(--lb-muted))]">
              All {TOOL_COUNT} tools, storage, AI tokens and limits across {MATRIX_TIERS.length} tiers.
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-auto">
            <ComparisonTable />
          </div>
        </DialogContent>
      </DialogPrimitive.Root>

      {/* MOBILE REVEAL (< sm) — full-bleed bottom sheet so the wide
          min-w-[820px] table gets the entire viewport width to scroll
          horizontally, which reads natively to thumb. Only one surface is ever
          mounted (chosen at open-time) so there's never a duplicate focus trap. */}
      <Sheet open={open && surface === "sheet"} onOpenChange={handleOpenChange}>
        <SheetContent
          side="bottom"
          onCloseAutoFocus={restoreFocus}
          className={cn(
            LB_TOKENS,
            "flex max-h-[88vh] flex-col gap-0 rounded-t-[1.75rem] border-t border-[hsl(var(--lb-line))] bg-[hsl(var(--lb-panel))] p-0 text-[hsl(var(--lb-ink))]",
          )}
        >
          <SheetHeader className="space-y-1 border-b border-[hsl(var(--lb-line))] px-5 py-5 pr-14 text-left">
            <div
              aria-hidden
              className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-[hsl(var(--lb-line))]"
            />
            <SheetTitle className="text-lg font-semibold tracking-[-0.01em] text-[hsl(var(--lb-ink))]">
              Plan comparison
            </SheetTitle>
            <SheetDescription className="text-sm text-[hsl(var(--lb-muted))]">
              All {TOOL_COUNT} tools, storage, AI tokens and limits across {MATRIX_TIERS.length}{" "}
              tiers. Scroll the table sideways to see more.
            </SheetDescription>
          </SheetHeader>

          <div className="min-h-0 flex-1 overflow-auto">
            <ComparisonTable />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
